/* eslint-disable @typescript-eslint/no-explicit-any */
import ImageOptimize from '@/components/ImageOptimize'
import { Loading } from '@/components/Loading/Loading'
import ProductCard from '@/components/ProductCard'
import { BannerProductListing } from '@/components/ProductListing/BannerProductListing'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import type { BannerList, Category, Promotion } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import Breadcrumb from '../Breadcrumb'
import Button from '../Button'
import ExpanableContent from '../ExpanableContent'
import HomePageBlogListWrapper from '../HomePageBlogList/HomePageBlogListWrapper'
import ProductCarousel from '../ProductCarousel'
import ProductCardSkeleton from '../Skeleton/ProductCardSkeleton'
import CategoryProductBrandFilters from './components/CategoryProductBrandFilters'
import CategoryProductFilters from './components/CategoryProductFilters'
import CategoryProductSort from './components/CategoryProductSort'
import ChildrenCategoryList from './components/ChildrenCategoryList'
import useCategoryProducts from './useCategoryProducts'

interface DynamicPageProps {
  pageDetail: any
  categoryDetail: Category
  banner: BannerList[]
  exclusiveDeal: Promotion
}

const CategoryPage = ({ categoryDetail, exclusiveDeal }: DynamicPageProps) => {
  const t = useTranslations()
  const tCate = useTranslations('CategoryDetail')
  const router = useRouter()

  const {
    products,
    loading,
    totalProducts,
    canLoadMore,
    loadingMore,
    limit,
    loadMore,
  } = useCategoryProducts(categoryDetail)

  const isChildCategory = !categoryDetail?.children?.length

  const haveOtherFilters = categoryDetail?.properties.some(
    x => !x.quickFilter && x.values?.length
  )

  return (
    <Main
      meta={
        <Meta
          title={
            getTranslate(categoryDetail?.metaTitle) &&
            getTranslate(categoryDetail?.metaTitle) !== ''
              ? getTranslate(categoryDetail?.metaTitle)
              : getTranslate(categoryDetail?.name)
          }
          description={
            getTranslate(categoryDetail?.metaDescription) &&
            getTranslate(categoryDetail?.metaDescription) !== ''
              ? getTranslate(categoryDetail?.metaDescription)
              : getTranslate(categoryDetail?.description)
          }
          noFollow={categoryDetail?.noFollow}
          noIndex={categoryDetail?.noIndex}
          canonical={
            categoryDetail?.canonicalUrl ||
            process.env.NEXT_PUBLIC_URL_WEBSITE + router.asPath
          }
          imageUrl={categoryDetail.image}
          imageAlt={getTranslate(categoryDetail.name)}
          mobileAlternate={{
            media: 'only screen and (max-width:991px))',
            href:
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE + router.asPath ||
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE ||
              '',
          }}
        />
      }
    >
      <div className='flex gap-1 py-5'>
        <Breadcrumb translateText={t('ProductDetail.Homepage')} />
        <span>/</span>
        <span>{getTranslate(categoryDetail.name)}</span>
      </div>

      <div className='flex flex-col gap-5'>
        <BannerProductListing data={categoryDetail.productCatBanners} />

        <ChildrenCategoryList data={categoryDetail?.children} />

        <ProductCarousel data={exclusiveDeal} hideViewAll />

        <CategoryProductBrandFilters categoryDetail={categoryDetail} />

        <div
          className={`flex w-full flex-row gap-5 ${
            !isChildCategory ? 'pt-2' : 'pt5'
          }`}
        >
          {isChildCategory && haveOtherFilters && (
            <div className='sticky top-0 z-10 h-fit w-1/3 rounded-xl bg-white pb-20'>
              <CategoryProductFilters categoryDetail={categoryDetail} />
            </div>
          )}

          <div className='cateProductList w-full pt-3' id='scrollableDiv'>
            <div className='flex items-center justify-between text-sm text-main'>
              <h1>
                Tìm thấy <strong>{totalProducts}</strong> sản phẩm
              </h1>
              <CategoryProductSort />
            </div>

            {loading && !loadingMore && (
              <div className='products mt-[30px] grid grid-cols-4 gap-[32px]'>
                {[...new Array(limit)].map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!!products?.length && (
              <>
                <div className='products mt-4 grid grid-cols-4 gap-3'>
                  {products.map((product, idx) => (
                    <ProductCard
                      product={product}
                      key={product._id + idx + 'list'}
                    />
                  ))}
                </div>

                {canLoadMore && (
                  <Button
                    variant='outlined'
                    className='mx-auto mt-3 w-[254px] !border-none bg-white'
                    onClick={loadMore}
                  >
                    {loading && <Loading />}
                    {!loading && (
                      <span className='text-center text-base font-normal leading-normal text-primary'>
                        Xem thêm {totalProducts - products.length} kết quả
                      </span>
                    )}
                  </Button>
                )}
              </>
            )}

            {products && products.length == 0 && !loading && (
              <div className='mt-[44px] flex flex-col items-center justify-center'>
                <ImageOptimize
                  src='/no-product.svg'
                  alt='Không tìm thấy sản phẩm phù hợp'
                  width={500}
                  height={372}
                />
                <span className='mt-[20px] text-HeadlineMedium font-normal'>
                  {tCate('notFound')}
                </span>
              </div>
            )}
          </div>
        </div>

        <ExpanableContent content={getTranslate(categoryDetail?.content)} />

        <HomePageBlogListWrapper />
      </div>
    </Main>
  )
}

export default CategoryPage
